import Quill from 'quill';

/**
 * QuillSharedToolbarManager
 * - Moves the toolbar of the active Quill editor into the shared container (#quill-shared-toolbar)
 * - Prevents toolbar blinking by immediately hiding inline toolbars
 * - Ensures the toolbar switches only when user interacts with an editor
 * 
 * KEY FIXES FOR ISSUES:
 * 1. Immediately hide toolbar inline to prevent visible blinking
 * 2. Only activate toolbar on user interaction (selection-change), not on editor creation
 * 3. Don't focus editors on load - let user click to focus
 */
class QuillSharedToolbarManager {
  private active?: Quill;
  private pendingActivations = new Set<Quill>(); // Track pending toolbar moves to batch them

  registerEditor(quill: Quill, id: String) {
    try {
      const toolbar = quill.getModule('toolbar') as any;
      if (toolbar && toolbar.container) {
        const toolbarEl: HTMLElement = toolbar.container as HTMLElement;
        // ✅ FIX BLINKING: Immediately hide inline toolbar in editor to prevent it from showing
        toolbarEl.style.display = 'none';
      }
    } catch {}

    // When user focuses/selects in editor, activate its toolbar (only on user interaction)
    quill.on('selection-change', (_range, _old, source) => {
      if (source === 'user') {
        // Only activate if user explicitly interacted
        this.activateFor(quill, id);
      }
    });

    // ✅ FIX AUTOFOCUS: Don't focus or set selection on editor initialization
    // User must click to focus - this prevents auto-focus on last editor
    // Do NOT call quill.focus() or quill.setSelection() here
  }

  activateFor(quill: Quill, id: String) {
    try {
      const toolbar = quill.getModule('toolbar') as any;
      if (!toolbar || !toolbar.container) return;

      const toolbarEl: HTMLElement = toolbar.container as HTMLElement;
      const host = document.querySelector('#' + id) as HTMLElement | null;
      if (!host) return;

      // ✅ PREVENT TOOLBAR BLINKING: Only move toolbar if not already there
      const isAlreadyInHost = toolbarEl.parentElement === host;
      
      if (!isAlreadyInHost) {
        // Clear host and move toolbar
        host.innerHTML = '';
        host.appendChild(toolbarEl);
      }

      // Make toolbar visible in shared location
      toolbarEl.style.display = 'block';
      host.style.display = 'block';
      
      // Hide all OTHER editors' toolbars (in case multiple are active)
      try {
        const allToolbars = document.querySelectorAll('.ql-toolbar');
        allToolbars.forEach((tb: Element) => {
          const tbEl = tb as HTMLElement;
          // Hide toolbar if it's inside an editor container (not in shared toolbar)
          if (tbEl !== toolbarEl && !host.contains(tbEl)) {
            tbEl.style.display = 'none';
          }
        });
      } catch {}
      
      this.active = quill;
      
      // ✅ EXPOSE active editor globally for image upload handler to use
      // This ensures image buttons in shared toolbar route to the current active editor
      (window as any).__activeQuillEditor = quill;
    } catch (err) {
      console.warn('[quillSharedToolbar] Error in activateFor:', err);
    }
  }
}

export const quillSharedToolbar = new QuillSharedToolbarManager();
